import { useCallback } from 'react';
import { QueryParamValueProps, useQueryParamsHandler } from 'odfjell-query-manager';

export const VOYAGE_NUMBERS_PARAM = 'voyageNumbers';

const canonicalizeVoyages = (voyages: string[]): string[] =>
    Array.from(new Set(voyages.map((v) => v.trim()).filter(Boolean))).sort((a, b) => Number(a) - Number(b));

/**
 * Shared hook for reading and writing the `voyageNumbers` URL query parameter.
 *
 * Format: comma-separated voyage numbers, canonically sorted and deduplicated.
 * Example: `?voyageNumbers=202501,202502`
 *
 * Writes are additive (no clear-all) so other params are never disturbed.
 */
const useVoyageUrlParams = () => {
    const { getQueryParamValueFromUrl, triggerQueryParamsUpdate } = useQueryParamsHandler();

    /** Returns the current voyage numbers from the URL, or null if the param is absent/empty. */
    const getVoyageNumbers = useCallback((): string[] | null => {
        const raw = getQueryParamValueFromUrl(VOYAGE_NUMBERS_PARAM);
        if (!raw) return null;
        const parts = raw
            .split(',')
            .map((v) => v.trim())
            .filter(Boolean);
        if (parts.length === 0) return null;
        return canonicalizeVoyages(parts);
    }, [getQueryParamValueFromUrl]);

    /**
     * Writes voyage numbers to the URL without disturbing other params.
     * Passing null or an empty array removes the param.
     */
    const setVoyageNumbers = useCallback((voyages: string[] | null) => {
        const params: QueryParamValueProps[] = [];
        if (voyages && voyages.length > 0) {
            params.push({ name: VOYAGE_NUMBERS_PARAM, newValue: canonicalizeVoyages(voyages).join(',') });
        } else {
            params.push({ name: VOYAGE_NUMBERS_PARAM, clearParam: true });
        }
        triggerQueryParamsUpdate(params);
    }, [triggerQueryParamsUpdate]);

    /** Removes the voyageNumbers param from the URL without disturbing other params. */
    const clearVoyageNumbers = useCallback(() => {
        triggerQueryParamsUpdate([{ name: VOYAGE_NUMBERS_PARAM, clearParam: true }]);
    }, [triggerQueryParamsUpdate]);

    return { getVoyageNumbers, setVoyageNumbers, clearVoyageNumbers };
};

export default useVoyageUrlParams;
